import { DeviceMetricsTable, tableColumns } from './device-metrics-table.tsx';
import { DeviceMetricsTableRowSubComponent } from './device-metrics-table-row-sub-component.tsx';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { DeviceTypeNames } from '@/features/devices/schemas/devices.schemas.ts';
import { cn } from '@/utils/utils.ts';
import { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button.tsx';
import { useSession } from '@/services/providers/session-provider.tsx';

type TDevice = {
  unit: string;
  field: number;
  time: string;
  value: number;
  last24Hours: TDevice[];
};

export const DeviceMetricsTableCard = ({
  data,
  plantId,
  deviceType,
  deviceSN,
  title,
  isLoading,
  className,
  expandable = {
    isExpandable: false,
    defaultExpanded: true,
  },
}: {
  data: TDevice[] | undefined;
  plantId: string | undefined;
  deviceType: DeviceTypeNames | undefined;
  deviceSN?: string;
  title?: string;
  isLoading: boolean;
  className?: string;
  expandable?: {
    isExpandable: boolean;
    defaultExpanded: boolean;
    onExpandChange?: (isExpanded: boolean) => void;
  };
}) => {
  const [isExpanded, setIsExpanded] = useState(expandable.defaultExpanded);
  const cardRef = useRef<HTMLDivElement>(null);

  const { role } = useSession();

  const handleClick = useCallback(() => {
    setIsExpanded((prev) => {
      const newValue = !prev;
      expandable.onExpandChange?.(newValue);
      return newValue;
    });
    setTimeout(() => {
      if (cardRef.current) {
        cardRef.current.focus();
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }, [expandable]);

  return (
    <DashboardCard
      className={cn(isExpanded && 'min-h-[464px]', className)}
      isLoading={isLoading}
    >
      <div ref={cardRef}>
        <div>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xl font-semibold text-nowrap">
              {title ? title : `Overview ${deviceType}`}
            </h3>
            {expandable.isExpandable && (
              <Button variant="secondary" className="p-2" onClick={handleClick}>
                {isExpanded ? (
                  <ChevronUpIcon className="size-5" />
                ) : (
                  <ChevronDownIcon className="size-5" />
                )}
              </Button>
            )}
          </div>
          <div
            className={cn(
              'grid overflow-hidden transition-all duration-300 ease-in-out',
              isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            {deviceType && isExpanded && (
              <DeviceMetricsTable
                data={data || []}
                columns={tableColumns(deviceType, role)}
                getRowCanExpand={() => true}
                RenderSubComponent={DeviceMetricsTableRowSubComponent}
                plantId={plantId || ''}
                deviceType={deviceType}
                deviceSN={deviceSN}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardCard>
  );
};
